import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Typography,
	Paper,
	Grid,
	Chip,
	Button,
	CircularProgress,
	Alert,
	Divider,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Tabs,
	Tab,
	Card,
	CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { talentPoolService } from '../../../services';

/**
 * TalentPool-hakemuksen yksityiskohtanäkymä
 * Näyttää hakemuksen tiedot, tekoälyn arvioinnin ja vastaukset
 * Mahdollistaa hakemuksen tilan muuttamisen
 */

// TabPanel-komponentti välilehtien sisällölle
const TabPanel = ({ children, value, index, ...props }) => (
	<div
		role="tabpanel"
		hidden={value !== index}
		id={`tabpanel-${index}`}
		{...props}
	>
		{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
	</div>
);

const TalentPoolApplicationDetail = () => {
	const { applicationId } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [status, setStatus] = useState(null);
	const [activeTab, setActiveTab] = useState(0);

	console.log('Hakemuksen ID:', applicationId);

	// Haetaan hakemuksen tiedot
	const {
		data: application,
		isLoading,
		error,
	} = useQuery(
		['talentPoolApplication', applicationId],
		() => talentPoolService.getTalentPoolApplication(applicationId),
		{
			staleTime: 5 * 60 * 1000, // 5 minuuttia
			onSuccess: (data) => {
				console.log('Hakemus ladattu:', data);
			},
			onError: (err) => {
				console.error('Virhe hakemuksen latauksessa:', err);
			},
		},
	);

	// Päivitetään hakemuksen tila
	const updateStatusMutation = useMutation(
		(newStatus) => {
			// Varmistetaan että newStatus on numero
			const statusNumber = Number(newStatus);
			return talentPoolService.updateTalentPoolApplicationStatus(
				applicationId,
				statusNumber,
			);
		},
		{
			onSuccess: (data) => {
				console.log('Tila päivitetty onnistuneesti:', data);
				// Invalidoidaan tarvittavat kyselyt päivityksen jälkeen
				queryClient.invalidateQueries([
					'talentPoolApplication',
					applicationId,
				]);
				queryClient.invalidateQueries(['talentPoolApplications']);
			},
			onError: (err) => {
				console.error('Virhe tilan päivityksessä:', err);
			},
		},
	);

	// Asetetaan tilalle alkuarvo kun hakemus on ladattu
	useEffect(() => {
		if (application && application.status !== undefined) {
			setStatus(application.status);
		}
	}, [application]);

	const handleStatusChange = (event) => {
		const newStatus = Number(event.target.value);
		setStatus(newStatus);
	};

	const handleStatusUpdate = () => {
		if (status !== null && status !== application?.status) {
			updateStatusMutation.mutate(status);
		}
	};

	// Tilavalinnat - Tyypit jotka ovat yhteensopivia MUI:n kanssa
	const statusOptions = [
		{ value: 1, label: 'Arvioitu', chipColor: 'primary' },
		{ value: 2, label: 'Hyväksytty', chipColor: 'success' },
		{ value: 3, label: 'Hylätty', chipColor: 'error' },
	];

	// Muodosta tila-chip
	const getStatusChip = (statusValue) => {
		const statusOption = statusOptions.find(
			(option) => option.value === statusValue,
		);
		if (!statusOption) return null;

		return (
			<Chip
				label={statusOption.label}
				color={statusOption.chipColor}
				size="small"
			/>
		);
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	if (isLoading)
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);

	if (error)
		return (
			<Alert severity="error" sx={{ m: 4 }}>
				Virhe hakemuksen lataamisessa:{' '}
				{error instanceof Error
					? error.message
					: 'Tarkista verkkoyhteytesi ja hakemuksen tunniste'}
			</Alert>
		);

	if (!application)
		return (
			<Alert severity="info" sx={{ m: 4 }}>
				Hakemusta ei löytynyt tunnuksella {applicationId}
			</Alert>
		);

	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate('/app/talentpool-applications')}
					variant="outlined"
				>
					Takaisin
				</Button>
				<Typography variant="h4">Hakemuksen tiedot</Typography>
				<Typography variant="caption" color="text.secondary">
					ID: {application.id || application._id}
				</Typography>
			</Box>

			{/* Basic Info Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				{/* Hakijan perustiedot */}
				<Grid item xs={12} md={4}>
					<Card elevation={2}>
						<CardContent>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									mb: 2,
									gap: 1,
								}}
							>
								<PersonIcon color="primary" />
								<Typography variant="h6">
									Hakijan tiedot
								</Typography>
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1.5,
								}}
							>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Nimi
									</Typography>
									<Typography
										variant="body1"
										fontWeight="medium"
									>
										{application.name}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Sähköposti
									</Typography>
									<Typography variant="body1">
										{application.email}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Puhelin
									</Typography>
									<Typography variant="body1">
										{application.phone}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Kaupunki
									</Typography>
									<Typography variant="body1">
										{application.city}
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Koulutustiedot */}
				<Grid item xs={12} md={4}>
					<Card elevation={2}>
						<CardContent>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									mb: 2,
									gap: 1,
								}}
							>
								<SchoolIcon color="primary" />
								<Typography variant="h6">
									Koulutustiedot
								</Typography>
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1.5,
								}}
							>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Oppilaitos
									</Typography>
									<Typography variant="body1">
										{application.university}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Pääaine
									</Typography>
									<Typography variant="body1">
										{application.major}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Sivuaine
									</Typography>
									<Typography variant="body1">
										{application.minor}
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Opintovaihe
									</Typography>
									<Typography variant="body1">
										{application.stage}
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Hakemuksen tila */}
				<Grid item xs={12} md={4}>
					<Card elevation={2}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Hakemuksen tila
							</Typography>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 2,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 2,
									}}
								>
									<Typography>Nykyinen tila:</Typography>
									{getStatusChip(application.status)}
								</Box>

								<FormControl fullWidth>
									<InputLabel>Muuta tilaa</InputLabel>
									<Select
										value={status ?? ''}
										onChange={handleStatusChange}
										label="Muuta tilaa"
									>
										{statusOptions.map((option) => (
											<MenuItem
												key={option.value}
												value={option.value}
											>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<Button
									variant="contained"
									onClick={handleStatusUpdate}
									disabled={
										status === application.status ||
										updateStatusMutation.isLoading
									}
									fullWidth
								>
									{updateStatusMutation.isLoading
										? 'Päivitetään...'
										: 'Päivitä tila'}
								</Button>

								{updateStatusMutation.isError && (
									<Alert severity="error">
										Virhe tilan päivityksessä
									</Alert>
								)}
								{updateStatusMutation.isSuccess && (
									<Alert severity="success">
										Tila päivitetty onnistuneesti
									</Alert>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Tabs */}
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					aria-label="hakemuksen tiedot"
				>
					<Tab icon={<AssessmentIcon />} label="Tekoälyn arviointi" />
					<Tab
						icon={<QuestionAnswerIcon />}
						label="Hakemuksen vastaukset"
					/>
				</Tabs>
			</Box>

			{/* Tekoälyn arviointi */}
			<TabPanel value={activeTab} index={0}>
				<Paper sx={{ p: 3 }}>
					<Box
						sx={{
							mb: 3,
							display: 'flex',
							alignItems: 'center',
							gap: 1,
						}}
					>
						<Typography variant="h6">Kokonaispisteet:</Typography>
						<Chip
							label={application.score}
							color="primary"
							sx={{ fontSize: '1rem', fontWeight: 'bold' }}
						/>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ ml: 2 }}
						>
							(maksimi: 24 pistettä)
						</Typography>
					</Box>

					<Grid container spacing={3}>
						{application.evaluationResults &&
							Object.entries(application.evaluationResults).map(
								([key, value]) => {
									// Määrittele MUI:n Chip-komponentin color-arvo
									const chipColor =
										value.score >= 3
											? 'success'
											: value.score < 2
												? 'warning'
												: 'primary';

									return (
										<Grid item xs={12} md={6} key={key}>
											<Paper sx={{ p: 2 }} elevation={1}>
												<Box
													sx={{
														display: 'flex',
														justifyContent:
															'space-between',
														alignItems: 'center',
														mb: 1,
													}}
												>
													<Typography
														variant="subtitle1"
														fontWeight="medium"
													>
														{key
															.replace(/_/g, ' ')
															.replace(
																/\b\w/g,
																(l) =>
																	l.toUpperCase(),
															)}
													</Typography>
													<Chip
														label={value.score}
														color={chipColor}
														size="small"
														sx={{
															fontWeight: 'bold',
														}}
													/>
												</Box>
												<Typography variant="body2">
													{value.justification}
												</Typography>
											</Paper>
										</Grid>
									);
								},
							)}
					</Grid>
				</Paper>
			</TabPanel>

			{/* Hakemuksen vastaukset */}
			<TabPanel value={activeTab} index={1}>
				<Paper sx={{ p: 3 }}>
					{application.formattedResponses?.map((response, index) => (
						<Box key={index} sx={{ mb: 4 }}>
							<Typography
								variant="subtitle1"
								sx={{
									fontWeight: 'bold',
									mb: 2,
								}}
							>
								{response.question.title}
							</Typography>
							<Typography variant="body1" sx={{ mb: 3 }}>
								{response.answer.value}
							</Typography>
							{index <
								application.formattedResponses.length - 1 && (
								<Divider />
							)}
						</Box>
					))}
					{(!application.formattedResponses ||
						application.formattedResponses.length === 0) && (
						<Alert severity="info">
							Tarkempia vastauksia ei ole saatavilla
						</Alert>
					)}
				</Paper>
			</TabPanel>
		</Box>
	);
};

export default TalentPoolApplicationDetail;
